import { useState, useEffect } from 'react';
import ImageUploader from '@/components/base/ImageUploader';
import { useBranding } from '@/hooks/useBranding';
import { updateRestaurantBranding } from '@/lib/brandingService';
import { invalidateSiteSettings } from '@/hooks/useSiteSettings';
import type { LogoPlacement } from '@/types/branding';
import RestaurantLogo from '@/components/branding/RestaurantLogo';

const BrandingTab = () => {
  const { restaurantLogoUrl, logoSize, logoPlacement, refreshConfig } = useBranding();
  const [logoUrl, setLogoUrl] = useState('');
  const [size, setSize] = useState(112);
  const [placement, setPlacement] = useState<LogoPlacement>('left');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    setLogoUrl(restaurantLogoUrl);
    setSize(logoSize);
    setPlacement(logoPlacement);
  }, [restaurantLogoUrl, logoSize, logoPlacement]);

  const showToast = (type: 'success' | 'error', text: string) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateRestaurantBranding({
        restaurant_logo_url: logoUrl,
        logo_size: size,
        logo_placement: placement,
      });
      invalidateSiteSettings();
      await refreshConfig();
      showToast('success', 'Marka ayarları kaydedildi.');
    } catch {
      showToast('error', 'Kayıt sırasında hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl flex flex-col gap-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Markalama & Görsel Yönetimi</h2>
        <p className="text-gray-500 text-sm">
          Restoran logonuzu yükleyin, boyutunu ve web sitesindeki hizalamasını ayarlayın.
        </p>
      </div>

      {toast && (
        <div
          className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium ${
            toast.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-100'
              : 'bg-red-50 text-red-600 border border-red-100'
          }`}
        >
          <i className={toast.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} />
          {toast.text}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 p-6 flex flex-col gap-6">
        <ImageUploader
          value={logoUrl}
          onChange={setLogoUrl}
          label="Restoran Logosu"
          folder="branding"
          placeholder="Logo URL veya dosya yükleyin"
        />

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold text-gray-600">Logo Boyutu (navbar / footer)</label>
            <span className="text-sm font-bold text-orange-500 tabular-nums">{size}px</span>
          </div>
          <input
            type="range"
            min={80}
            max={200}
            step={4}
            value={size}
            onChange={(e) => setSize(parseInt(e.target.value, 10))}
            className="w-full accent-orange-500"
          />
          <div className="flex justify-between text-[10px] text-gray-400 mt-1">
            <span>80px</span>
            <span>200px</span>
          </div>
          <p className="text-[11px] text-gray-400 mt-1.5">
            Dikey logolar için 110–140 px genelde iyi görünür.
          </p>
          <input
            type="number"
            min={80}
            max={220}
            value={size}
            onChange={(e) => setSize(Math.max(80, Math.min(220, parseInt(e.target.value, 10) || 112)))}
            className="mt-2 w-24 px-3 py-2 rounded-xl border border-gray-200 text-sm outline-none focus:border-orange-400"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-2">Web Sitesi Hizalama</label>
          <div className="grid grid-cols-2 gap-3">
            {(['left', 'center'] as LogoPlacement[]).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPlacement(p)}
                className={`py-3 rounded-xl text-sm font-semibold cursor-pointer transition-all border ${
                  placement === p
                    ? 'bg-orange-500 text-white border-orange-500'
                    : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-orange-300'
                }`}
              >
                <i className={`${p === 'left' ? 'ri-align-left' : 'ri-align-center'} mr-1.5`} />
                {p === 'left' ? 'Sol' : 'Orta'}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-xl bg-gray-50 border border-gray-100 p-5">
          <p className="text-xs font-semibold text-gray-500 mb-3 uppercase tracking-wider">Önizleme</p>
          <div
            className={`flex items-center min-h-[64px] px-4 py-3 bg-white rounded-lg border border-gray-200 ${
              placement === 'center' ? 'justify-center' : 'justify-start'
            }`}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Logo önizleme"
                className="object-contain"
                style={{ width: `${size}px`, maxWidth: '100%', height: 'auto' }}
              />
            ) : (
              <RestaurantLogo width={size} showPlaceholder />
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="self-start flex items-center gap-2 px-6 py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold cursor-pointer disabled:opacity-50"
        >
          {saving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
          Kaydet
        </button>
      </div>
    </div>
  );
};

export default BrandingTab;
